'use client';

import { FC, useMemo, useState } from 'react';

import { FacetFilterTracking, facetLabelTranslationMappingV2, BasicFacetWithTermsV2 } from '@ravago/shared/page-data/models/elastic';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { BreakpointSwitch } from '@ravago/shared/radiance/components/breakpoint-switch/components/BreakpointSwitch/BreakpointSwitch';
import { Expand } from '@ravago/shared/radiance/components/expand/components/Expand/Expand';
import { Icon } from '@ravago/shared/radiance/components/icon/components/Icon/Icon';
import { InputField } from '@ravago/shared/radiance/components/input-field/components/InputField/InputField';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useTranslations } from '@ravago/shared/translations/client';

import { FacetGroupStructureV2 } from '../../../models/facet-group.model';
import { getActiveFacetLabelsV2 } from '../../../utils/filter-label-utils';

import { FacetGroupTermListV2 } from './facet-group-term-list-v2';

interface Props {
   language: string;
   expanded: boolean;
   activeFacet?: BasicFacetWithTermsV2;
   activeNestedFacet?: BasicFacetWithTermsV2;
   facetGroupStructure: FacetGroupStructureV2;
   onFacetsChange: (value: BasicFacetWithTermsV2[], switchToList?: boolean) => void;
   onFacetClicked: (trackingFacet: FacetFilterTracking) => void;
   onExpandToggle: (expanded: boolean) => void;
}

export const FacetGroupV2: FC<Props> = ({
   language,
   expanded,
   activeFacet,
   facetGroupStructure,
   onFacetsChange,
   onFacetClicked,
   activeNestedFacet,
   onExpandToggle
}) => {
   const translate = useTranslations();
   const [searchValue, setSearchValue] = useState('');

   const activeFacetLabel = useMemo(
      () =>
         activeFacet
            ? getActiveFacetLabelsV2(
                 activeFacet,
                 facetGroupStructure
              )
            : undefined,
      [activeFacet, facetGroupStructure]
   );

   if (!facetGroupStructure.facets.length) return null;

   return (
      <Box direction="column" gap="sm">
         <Expand
            expanded={expanded}
            onExpandToggle={onExpandToggle}
            header={
               <Box justify="between" items="baseline" spacing={{ top: 'sm', bottom: 'sm' }}>
                  <Box direction="column" gap="none">
                     <Typography variant="body" color="strong">
                        {translate(facetLabelTranslationMappingV2.get(facetGroupStructure.identifier.key) ?? facetGroupStructure.identifier.key)}
                     </Typography>
                     {activeFacetLabel && (
                        <BreakpointSwitch excludeViews={['desktop']}>
                           <Typography variant="small" color="subtle" maxLines="1">
                              {activeFacetLabel}
                           </Typography>
                        </BreakpointSwitch>
                     )}
                  </Box>
                  <Icon color="subtle" icon={expanded ? 'chevron-up' : 'chevron-down'} />
               </Box>
            }
         >
            <Box direction="column" spacing={{ top: 'xs', bottom: 'xs' }}>
               <InputField
                  id={`${facetGroupStructure.identifier.key}-search`}
                  label="Search"
                  trailingIcon="search"
                  onChange={setSearchValue}
                  value={searchValue}
                  clearable
               />
            </Box>

            <FacetGroupTermListV2
               language={language}
               searchValue={searchValue}
               activeFacet={activeFacet}
               isCollectionGroup={facetGroupStructure.identifier.collectionGroup}
               facetGroupStructure={facetGroupStructure}
               activeNestedFacet={activeNestedFacet}
               onFacetChange={onFacetsChange}
               onFacetClicked={onFacetClicked}
            />
         </Expand>
      </Box>
   );
};
